// iter150 — Public Terms & Conditions page for Twilio A2P 10DLC compliance.
// MUST render publicly with NO authentication redirect. Contains every
// SMS-program element Twilio's compliance review requires:
//   - Program description
//   - "Message frequency varies"
//   - "Message and data rates may apply"
//   - Reply STOP to unsubscribe
//   - Reply HELP for assistance
//   - Explicit customer consent language
//   - Link to Privacy Policy

const TERMS_LAST_UPDATED = "February 28, 2026";

export default function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="terms-page">
      <header className="border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="terms-home-link">
            <div className="w-9 h-9 rounded-full bg-[#047857] text-white flex items-center justify-center font-extrabold">J</div>
            <div className="font-extrabold tracking-tight text-gray-900">JDSN Lawn Care</div>
          </a>
          <a href="/" className="text-[13px] font-bold text-[#047857] hover:underline">← Back to home</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6 leading-relaxed">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Legal</div>
          <h1 className="mt-1 text-4xl sm:text-5xl font-extrabold tracking-tight font-[Outfit]">Terms &amp; Conditions</h1>
          <p className="mt-2 text-sm text-gray-500">Last updated: {TERMS_LAST_UPDATED}</p>
        </div>

        <p>
          By using JDSN Lawn Care&apos;s website, booking system, or by opting in to
          receive SMS from us, you agree to these Terms &amp; Conditions. Please read
          them carefully. If you do not agree, please do not use our services.
        </p>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">1. Services</h2>
          <p>
            JDSN Lawn Care provides lawn-care and property-maintenance services in the
            Jacksonville, Florida area — including lawn mowing, edging, weed eating,
            bush &amp; hedge trimming, cleanups, mulching, pressure washing, and related
            work. All services are subject to weather, safety, property access, and crew
            availability. Prices are the customer-agreed amount shown at booking
            confirmation and remain locked unless the customer or an admin explicitly
            changes address, yard area, frequency, services, or add-ons.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">2. Booking &amp; payment</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Bookings are confirmed by email and SMS once accepted.</li>
            <li>Payment is processed by Stripe. We accept major credit / debit cards.</li>
            <li>For recurring services, your saved payment method is charged automatically after each completed visit.</li>
            <li>Refunds and re-services are handled case-by-case; contact us within 48&nbsp;hours of the visit.</li>
          </ul>
        </section>

        <section className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-5">
          <h2 className="text-xl font-extrabold tracking-tight mb-2">3. SMS program terms (Twilio A2P 10DLC)</h2>

          <p className="font-bold">Program description</p>
          <p>
            JDSN Lawn Care operates a customer-service SMS program. When you provide
            your mobile number and opt in — by checking the SMS-consent checkbox on our
            booking form or by explicitly requesting text updates on a service — you
            agree to receive text messages from JDSN Lawn Care related to your service,
            including booking confirmations, appointment reminders, crew arrival &amp;
            completion notices, payment receipts, payment-failure notices, and
            occasional service updates.
          </p>

          <p className="font-bold mt-4">Consent</p>
          <p>
            You expressly consent to receive automated text messages from JDSN Lawn
            Care at the mobile number you provide. Consent to receive SMS is
            <strong> not a condition of purchase</strong>. You may opt out at any time.
          </p>

          <p className="font-bold mt-4">Message frequency</p>
          <p>
            <strong>Message frequency varies</strong> and depends on your usage — for
            example, one confirmation SMS per booking and one reminder per scheduled
            visit. Typically fewer than 8 messages per month for active recurring
            customers.
          </p>

          <p className="font-bold mt-4">Fees</p>
          <p>
            <strong>Message and data rates may apply.</strong> Any charges are from
            your mobile carrier; JDSN Lawn Care does not charge you for SMS.
          </p>

          <p className="font-bold mt-4">Opting out — STOP</p>
          <p>
            You can cancel the SMS service at any time by replying <strong>STOP</strong>
            to any message we send you. After you send SMS message <strong>STOP</strong>
            to us, we will send you an SMS message to confirm that you have been
            unsubscribed. After this, you will no longer receive SMS messages from us.
            If you want to join again, sign up as you did the first time and we will
            start sending SMS messages to you again.
          </p>

          <p className="font-bold mt-4">Help — HELP</p>
          <p>
            If you are experiencing issues with the messaging program you can reply
            <strong> HELP</strong> for additional assistance, or you can get help
            directly at <a href="tel:9048328730" className="text-[#047857] font-bold">(904) 832-8730</a>
            or <a href="mailto:jdsnlawncare@gmail.com" className="text-[#047857] font-bold">jdsnlawncare@gmail.com</a>.
          </p>

          <p className="font-bold mt-4">Carriers</p>
          <p>
            Carriers are not liable for delayed or undelivered messages. Supported
            carriers include AT&amp;T, Verizon, T-Mobile, Sprint, and other major US
            carriers.
          </p>

          <p className="font-bold mt-4">Privacy</p>
          <p>
            For information on how we handle your data, see our{" "}
            <a href="/privacy-policy" className="text-[#047857] font-bold underline">Privacy Policy</a>.
            Your mobile phone number and SMS consent are never sold, rented, or shared
            with third parties for their own marketing purposes.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">4. Cancellations &amp; changes</h2>
          <p>
            You can skip a visit, change frequency, or cancel your recurring service
            at any time from your customer portal or by contacting us. Completed
            visits and paid invoices remain final.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">5. Property access &amp; damage</h2>
          <p>
            You are responsible for ensuring safe access to the property (unlocked
            gates, pets secured, sprinkler heads marked). JDSN Lawn Care carries
            liability insurance; report any damage within 48&nbsp;hours of the visit so
            we can investigate.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">6. Limitation of liability</h2>
          <p>
            To the fullest extent permitted by law, JDSN Lawn Care&apos;s total
            liability arising from any service is limited to the amount paid for that
            service. We are not liable for indirect, incidental, or consequential
            damages.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">7. Changes to these terms</h2>
          <p>
            We may update these Terms from time to time. Material changes will be
            posted on this page with an updated &quot;Last updated&quot; date.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">8. Contact us</h2>
          <p>
            JDSN Lawn Care · Jacksonville, Florida<br />
            Phone: <a href="tel:9048328730" className="text-[#047857] font-bold">(904) 832-8730</a><br />
            Email: <a href="mailto:jdsnlawncare@gmail.com" className="text-[#047857] font-bold">jdsnlawncare@gmail.com</a>
          </p>
          <p className="mt-2 text-sm text-gray-600">
            See also our <a href="/privacy-policy" className="text-[#047857] font-bold">Privacy Policy</a>.
          </p>
        </section>
      </main>

      <footer className="border-t border-gray-100 mt-8">
        <div className="max-w-3xl mx-auto px-6 py-6 text-xs text-gray-500 flex flex-wrap gap-3 justify-between">
          <span>© {new Date().getFullYear()} JDSN Lawn Care. All rights reserved.</span>
          <span className="flex gap-4">
            <a href="/privacy-policy" className="hover:text-[#047857] font-bold">Privacy Policy</a>
            <a href="/terms-and-conditions" className="hover:text-[#047857] font-bold">Terms &amp; Conditions</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
